import { Hooks, MapSpecModifiers, Utils } from '@platform/maplibre';
import { Buttons, MUIcon } from '@platform/shared/ui';
import { ProjectTypes } from '@platform/types';
import produce from 'immer';
import { LngLatLike, MapGeoJSONFeature } from 'maplibre-gl';
import React, { useEffect, useMemo, useState } from 'react';
import SocialExplorerWorkingLayerTooltipWrapper from './Tooltip/SocialExplorer/SocialExplorerWorkingLayerTooltipWrapper';
import UserUploadedWorkingLayerTooltipWrapper from './Tooltip/UserUpload/UserUploadedWorkingLayerTooltipWrapper';

const { useFeatureHighlightSingleMap } = Hooks;

export const REMOVE_TOOLTIPS_EVENT = 'REMOVE_TOOLTIPS_EVENT';

type FeaturesState = {
  latLong: LngLatLike | null;
  features: Record<string, MapGeoJSONFeature[]>;
};

const FEATURES_WITH_POINT_INITIAL_STATE = {
  latLong: null,
  features: {},
};

const useWorkingLayerInteractivity = (mapObj: ProjectTypes.MapObject, mode: 'view' | 'edit' | 'analyze') => {
  const inEditMode = mode === 'edit';

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [allFeatures, setAllFeatures] = useState<FeaturesState>(FEATURES_WITH_POINT_INITIAL_STATE);
  const [prevFeatures, setPrevFeatures] = useState<MapGeoJSONFeature[]>([]);

  const layerIds = useMemo(
    () => (inEditMode ? mapObj.styleSpec.workingLayers.map((x) => Utils.getWorkingLayerId(x)) : []),
    [mapObj.styleSpec.workingLayers, inEditMode]
  );

  useFeatureHighlightSingleMap(mapObj.ref, layerIds);

  useEffect(() => {
    const customHandler = () => setAllFeatures(FEATURES_WITH_POINT_INITIAL_STATE);
    mapObj.ref?.on(REMOVE_TOOLTIPS_EVENT, customHandler);
    return () => {
      mapObj.ref?.off(REMOVE_TOOLTIPS_EVENT, customHandler);
    };
  }, [mapObj.ref]);

  useEffect(() => {
    setAllFeatures(FEATURES_WITH_POINT_INITIAL_STATE);
  }, [mapObj.styleSpec.workingLayers.length]);

  useEffect(() => {
    if (!mapObj.ref) return;

    // clear
    prevFeatures
      .filter((f) => f.id)
      .forEach((f) => {
        mapObj.ref?.removeFeatureState({ source: f.source, sourceLayer: f.sourceLayer, id: f.id }, 'highlight');
      });

    const flattened = Object.values(allFeatures.features).flat();

    const activeFeature = flattened[activeIndex];
    if (activeFeature) {
      mapObj.ref?.setFeatureState(
        {
          source: activeFeature.source,
          sourceLayer: activeFeature.sourceLayer,
          id: activeFeature.id,
        },
        { ...(activeFeature.state ?? {}), highlight: true }
      );
    }

    setPrevFeatures(flattened);
  }, [mapObj.ref, activeIndex, allFeatures]);

  const handleFeaturesUnderMouse = (
    workingLayerId: string,
    features: MapGeoJSONFeature[],
    latLong: LngLatLike | null
  ) => {
    setActiveIndex(0);
    setAllFeatures((p) => ({ latLong: latLong, features: { ...p.features, [workingLayerId]: features } }));
  };

  const allFeaturesFlat = Object.values(allFeatures.features).flat();
  let actions: React.ReactNode;

  if (allFeaturesFlat.length > 1) {
    actions = (
      <div className="flex items-center justify-center gap-2 text-sm">
        <Buttons.Secondary
          icon={<MUIcon name="chevron_left" />}
          onClick={() => setActiveIndex((p) => Math.max(p - 1, 0))}
          className="h-[28px] w-[28px] rounded-full bg-gray-200 text-gray-700 ring-0 hover:bg-gray-100"
          disabled={activeIndex === 0}
        />
        <span className="flex-shrink-0 px-2">{`${activeIndex + 1} of ${allFeaturesFlat.length}`}</span>
        <Buttons.Secondary
          icon={<MUIcon name="chevron_right" />}
          onClick={() => setActiveIndex((p) => Math.min(p + 1, allFeaturesFlat.length - 1))}
          className="h-[28px] w-[28px] rounded-full bg-gray-200 text-gray-700 ring-0 hover:bg-gray-100"
          disabled={activeIndex === allFeaturesFlat.length - 1}
        />
      </div>
    );
  }

  return mapObj.styleSpec.workingLayers
    .filter((x) => inEditMode || x.tooltip.enabled)
    .map((x) => {
      const handleTooltipClose = () => setAllFeatures(FEATURES_WITH_POINT_INITIAL_STATE);

      let emptyPlaceholder = null;
      if (inEditMode && !x.tooltip?.title && !x.tooltip?.content.length) {
        emptyPlaceholder = <>Add a title and select variables to display in the popup.</>;
      }

      const handleFeatureSelectionChange = (
        wId: string,
        featureId: string,
        featureName: string,
        featureMeta?: object
      ) => {
        const workingLayer = mapObj.styleSpec.workingLayers.find((x) => x.id === wId);
        if (!workingLayer) return;

        const updatedLayer = produce(workingLayer, (draft) => {
          const existingIndex = draft.featureSelection.findIndex((x) => x.id === featureId);

          if (existingIndex > -1) {
            // feature under mouse is already in selection, remove it
            draft.featureSelection.splice(existingIndex, 1);
          } else {
            draft.featureSelection.push({ id: featureId, name: featureName, metadata: featureMeta });
          }
        });
        const updatedMapSpec = MapSpecModifiers.updateWorkingLayer(mapObj.styleSpec, updatedLayer);

        mapObj.updateMapSpec(updatedMapSpec);
      };

      if (x.source === 'social-explorer') {
        // TODO: refactor, align
        return (
          <SocialExplorerWorkingLayerTooltipWrapper
            key={x.id}
            enabled={mode === 'edit'}
            map={mapObj.ref}
            workingLayer={x as ProjectTypes.SocialExplorerWorkingLayer}
            inEditMode={inEditMode}
            onFeaturesUnderMouse={handleFeaturesUnderMouse}
            features={allFeatures.features[x.id] ?? []}
            latLong={allFeatures.latLong}
            activeFeature={allFeaturesFlat[activeIndex]}
            actions={actions}
            onClose={handleTooltipClose}
            onFeatureSelectionChange={inEditMode ? handleFeatureSelectionChange : undefined}
          />
        );
      }
      // TODO: refactor, align
      return (
        <UserUploadedWorkingLayerTooltipWrapper
          key={x.id}
          enabled={mode === 'edit'}
          map={mapObj.ref}
          workingLayer={x as ProjectTypes.UserUploadedWorkingLayer}
          inEditMode={inEditMode}
          onFeaturesUnderMouse={handleFeaturesUnderMouse}
          features={allFeatures.features[x.id] ?? []}
          latLong={allFeatures.latLong}
          activeFeature={allFeaturesFlat[activeIndex]}
          actions={actions}
          onClose={handleTooltipClose}
          emptyPlaceholder={emptyPlaceholder}
        />
      );
    });
};

export default useWorkingLayerInteractivity;
